import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import AuthCard from "../../components/layout/AuthCard";
import Input from "../../components/ui/Input";
import Button from "../../components/ui/Button";
import { setPassword } from "../../services/api";

// Screen A-2: First-time password set.
// Calls POST /web/v1/auth/change-password with the invite token.
// NOTE: coordinate with backend if the endpoint requires oldPassword + OTP instead.
const SetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || "";
  const token = location.state?.token || ""; // temp token from invite link

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [bannerError, setBannerError] = useState("");

  const validate = () => {
    const errs = {};
    if (!newPassword) errs.newPassword = "Password is required.";
    else if (newPassword.length < 8) errs.newPassword = "Password must be at least 8 characters.";
    if (!confirmPassword) errs.confirmPassword = "Please confirm your password.";
    else if (newPassword !== confirmPassword) errs.confirmPassword = "Passwords do not match.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setBannerError("");
    try {
      const res = await setPassword({ email, password: newPassword, token });
      if (res.status === 200) {
        navigate("/auth/login", { state: { email } });
      }
    } catch (err) {
      setBannerError(
        err?.response?.data?.message || "Failed to set password. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthCard>
      <h1 className="text-xl font-semibold text-textPrimary mb-1">Set your own password</h1>
      <p className="text-sm text-textMuted mb-6">
        It looks like you were initially given a system-generated password. Please create a new one for easier access next time.
      </p>

      {bannerError && (
        <div className="flex items-center gap-2 bg-statusRedBg border border-red-200 text-statusRed rounded-lg px-4 py-3 mb-5 text-sm">
          <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          {bannerError}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <div className="space-y-4">
          <Input
            label="New password"
            type="password"
            name="newPassword"
            value={newPassword}
            onChange={(e) => { setNewPassword(e.target.value); setErrors((p) => ({ ...p, newPassword: "" })); }}
            placeholder="Enter new password"
            hint="Minimum 8 characters"
            error={errors.newPassword}
            required
          />
          <Input
            label="Retype password"
            type="password"
            name="confirmPassword"
            value={confirmPassword}
            onChange={(e) => { setConfirmPassword(e.target.value); setErrors((p) => ({ ...p, confirmPassword: "" })); }}
            placeholder="Confirm your password"
            error={errors.confirmPassword}
            required
          />
        </div>
        <div className="mt-6 space-y-3">
          <Button type="submit" loading={loading}>Continue</Button>
          <div className="text-center">
            <Link to="/auth/login" className="text-sm text-brand hover:underline">
              Back to Login
            </Link>
          </div>
        </div>
      </form>
    </AuthCard>
  );
};

export default SetPassword;
